// @ts-strict-ignore
// keep this file isomorphic
import moment from 'moment';
import momentTimezone from 'moment-timezone';

import {Translatable} from 'common/i18n';
import {CALENDAR_DATE_FORMAT, TIMESTAMP_FORMAT, Timezone} from 'common/constants';
import {ScrollingCalendarDateSelection} from 'common/components/scrolling-calendar/util';
import {TimePeriodSettings} from 'common/report/stores/types';
import {
  assertIsStandardTimeUnit,
  combineDateWithTimeFromDate,
  DEFAULT_TIME_PERIOD_SETTINGS,
  getCurrentMomentAsOf,
  getLocalTimezone,
  parseDate,
  parseDateWithTimeZone,
} from 'common/util/date';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {
  EXCLUSION_SELECTED_LABEL,
  exclusionOffsetToPreset,
} from 'common/widgets/date-range-picker/models/exclusion-offset';
import {
  END_OF_TODAY,
  getPresetDateRangeFromAttributes,
  PRESET_DATE_RANGE_DISPLAY_NAMES,
  SPECIAL_DATE_DISPLAY_NAMES,
  START_OF_TODAY,
} from 'common/widgets/date-range-picker/models/presets';
import {
  DateRangeAttributes,
  DateRangeType,
  ExclusionPreset,
  RelativeDate,
  TimeUnit,
  TimeUnitLabelTranslatable,
} from 'common/widgets/date-range-picker/models/types';
import {getDateType} from 'common/widgets/date-range-picker/models/utils';

import {LAST_HOURS_MAX, LAST_MINUTES_MAX} from './constants';

export enum AdvancedMenuItem {
  EnableDisableTimePicker = `ENABLE_DISABLE_TIME_PICKER`,
}

// DatePickerMode is not supposed to map 1-1 with DateRangeType, it is purely for UI presentation
export enum DatePickerMode {
  InTheLast = `in the last`,
  Between = `between`,
  Since = `since`,
  On = `on`,
  RelativeFuture = `in the next`,
}

type DateRangeLabelFormatMode = `simple` | `sentence` | `none` | `numeric`;

export function getDatePickerMode(dateRange: DateRange): DatePickerMode {
  if (dateRange.isRolling()) {
    return DatePickerMode.InTheLast;
  }

  switch (dateRange.type) {
    case DateRangeType.RelativeBefore:
    case DateRangeType.RelativeAfter:
    case DateRangeType.NotRelativeAfter: {
      return DatePickerMode.InTheLast;
    }
    case DateRangeType.RelativeFuture: {
      return DatePickerMode.RelativeFuture;
    }
    case DateRangeType.Before:
    case DateRangeType.EndsOn:
    case DateRangeType.On:
    case DateRangeType.NotOn: {
      return DatePickerMode.On;
    }
    case DateRangeType.NotBetween:
    case DateRangeType.Between: {
      return DatePickerMode.Between;
    }
    case DateRangeType.StartsOn:
    case DateRangeType.Since: {
      return DatePickerMode.Since;
    }

    default:
      throw new Error(`"date-range" attribute contains DateRange info with unsupported DateRangeType`);
  }
}

export function getCalendarSelection(
  dateRange: DateRange,
  asOfDate: moment.Moment,
  timezone?: Timezone,
): ScrollingCalendarDateSelection {
  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(CALENDAR_DATE_FORMAT));
  let startDate = moment(parseDate(from));
  let endDate = moment(parseDate(to));

  if (timezone) {
    startDate = parseDateWithTimeZone(from, timezone);
    endDate = parseDateWithTimeZone(to, timezone);
  }

  // clamp endDate to startDate
  if (startDate.isSameOrAfter(endDate)) {
    endDate = moment(parseDate(from));
    if (timezone) {
      endDate = parseDateWithTimeZone(from, timezone);
    }
  }

  const offsetFrom = timezone ? asOfDate.format(`YYYY-MM-DD`) : asOfDate.toISOString();

  if (startDate.isSameOrBefore(endDate, `day`)) {
    const showOffset = dateRange.isRolling() && endDate.isBefore(asOfDate, `day`);
    const isRelativeFutureMode = getDatePickerMode(dateRange) === DatePickerMode.RelativeFuture;

    return {
      start: timezone ? startDate.format(`YYYY-MM-DD`) : startDate.toISOString(),
      end: timezone ? endDate.format(`YYYY-MM-DD`) : endDate.toISOString(),
      highlightStart: !isRelativeFutureMode,
      highlightEnd:
        isRelativeFutureMode ||
        (!endDate.isSame(startDate, `day`) && getDatePickerMode(dateRange) === DatePickerMode.Between),
      ...(showOffset ? {offsetFrom} : {}),
    };
  }

  return null;
}

export function getRelativeWindowUnit(dateRange: DateRange): TimeUnit {
  if (dateRange.window) {
    return dateRange.window.unit;
  }
  if (dateRange.isRolling()) {
    const relativeTo = dateRange.to as RelativeDate;
    if (relativeTo.unit) {
      return relativeTo.unit;
    }
  }
  return TimeUnit.Day;
}

export function getRelativeWindowValue(dateRange: DateRange, asOfDate: moment.Moment): number {
  if (dateRange.window) {
    return dateRange.window.value;
  } else if (
    (dateRange.from as RelativeDate)?.unit &&
    (dateRange.from as RelativeDate).unit === (dateRange.to as RelativeDate).unit
  ) {
    // Subtract the `to` offset from the `from` window value since the unit is the same.
    // This is more reliable than converting the date back and forth multiple times in the block below, since it may not account for the correct
    // start/end of unit alignment across all unit combinations.
    return (dateRange.from as RelativeDate)?.value - (dateRange.to as RelativeDate)?.value;
  }

  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(`YYYY-MM-DD`));
  const startDate = moment(parseDate(from));
  const endDate = moment(parseDate(to));
  const windowUnit = getRelativeWindowUnit(dateRange);
  assertIsStandardTimeUnit(windowUnit);
  return endDate.diff(startDate, windowUnit);
}

// returns offset if there is a valid offset
export function getRelativeOffset(dateRange: DateRange, asOfDate: moment.Moment): null | RelativeDate {
  if (dateRange.window?.unit === TimeUnit.Day) {
    return {unit: TimeUnit.Day, value: 0};
  }
  if (dateRange.isRolling()) {
    if (typeof dateRange.to === `object`) {
      const relativeTo = dateRange.to;
      return relativeTo;
    } else if (typeof dateRange.to === `string`) {
      const {to} = dateRange.getCalendarDatesAsOf(asOfDate.format(CALENDAR_DATE_FORMAT));
      const endDate = moment(parseDate(to));
      const value = asOfDate.diff(endDate, TimeUnit.Day);
      return {unit: TimeUnit.Day, value};
    }
  }
  return null;
}

// ------- note: always output DateRange attributes instead of DateRange class instances because the appropriate class is determined by display context, which is a UI concern -------

/** get date range attributes for "in the last" or "in the next" types */
export function getRelativeDateRangeAttributes(
  window: RelativeDate,
  offset: null | RelativeDate,
  datePickerMode: DatePickerMode.InTheLast | DatePickerMode.RelativeFuture,
): DateRangeAttributes {
  if (offset) {
    if (window.unit !== offset.unit) {
      throw new Error(`window and offset units must match`);
    }
    if (offset.unit !== TimeUnit.Day) {
      throw new Error(`offset unit must be Day`);
    }

    if (offset.value > 0) {
      const isSingleDay = window.value === 0 && window.unit === TimeUnit.Day;
      return {
        type: isSingleDay ? DateRangeType.On : DateRangeType.Between,
        to: offset,
        from: {
          unit: window.unit,
          value: offset.value + window.value,
        },
        rolling: true,
      };
    }
  }

  return {
    type: datePickerMode === DatePickerMode.RelativeFuture ? DateRangeType.RelativeFuture : DateRangeType.RelativeAfter,
    window,
  };
}

export function getBetweenDateRangeAttributes(
  fromDate: moment.Moment,
  toDate: moment.Moment,
  times?: {fromTime: moment.Moment; toTime: moment.Moment},
): DateRangeAttributes {
  const firstDate = fromDate.isSameOrBefore(toDate, `day`) || !toDate.isValid() ? fromDate : toDate;
  const secondDate = fromDate.isSameOrAfter(toDate, `day`) ? fromDate : toDate;

  if (times?.fromTime && times?.fromTime.isValid() && times?.toTime && times?.toTime.isValid()) {
    // When we pass times keep the times attached to their original positions (from/to), regardless
    // of whether we switch the dates.
    return {
      type: DateRangeType.Between,
      from: combineDateWithTimeFromDate(firstDate, times.fromTime).format(TIMESTAMP_FORMAT),
      to: combineDateWithTimeFromDate(secondDate, times.toTime).format(TIMESTAMP_FORMAT),
    };
  }
  return {
    type: DateRangeType.Between,
    from: firstDate.format(TIMESTAMP_FORMAT),
    to: secondDate.format(TIMESTAMP_FORMAT),
  };
}

export function getOnDateRangeAttributes(onDate: moment.Moment): DateRangeAttributes {
  return {
    type: DateRangeType.On,
    from: onDate.format(TIMESTAMP_FORMAT),
    to: onDate.format(TIMESTAMP_FORMAT),
  };
}

export function getSinceDateRangeAttributes(sinceDate: moment.Moment): DateRangeAttributes {
  return {
    type: DateRangeType.Since,
    from: sinceDate.format(TIMESTAMP_FORMAT),
  };
}

export function getSimilarDateRange(
  dateRange: DateRange,
  asOfDate: moment.Moment,
  type: DateRangeType,
  timezone?: Timezone,
  times?: {fromTime: moment.Moment; toTime: moment.Moment},
): DateRangeAttributes | null {
  const {from, to} = dateRange.getCalendarDatesAsOf(asOfDate.format(TIMESTAMP_FORMAT));
  let fromDate = moment(parseDate(from));
  let toDate = moment(parseDate(to));

  if (timezone) {
    fromDate = parseDateWithTimeZone(from, timezone);
    toDate = parseDateWithTimeZone(to, timezone);
  }

  switch (type) {
    case DateRangeType.RelativeBefore:
    case DateRangeType.RelativeAfter:
    case DateRangeType.NotRelativeAfter:
    case DateRangeType.RelativeFuture: {
      const windowValue = getRelativeWindowValue(dateRange, asOfDate);
      const windowUnit = getRelativeWindowUnit(dateRange);
      const offsetDays = asOfDate.diff(toDate, TimeUnit.Day);
      const canBeRolling = dateRange.canUseRolling() && windowUnit === TimeUnit.Day && offsetDays > 0;

      if (canBeRolling) {
        const isSingleDay = windowValue === 0 && windowUnit === TimeUnit.Day;
        return {
          type: isSingleDay ? DateRangeType.On : DateRangeType.Between,
          to: {
            unit: windowUnit,
            value: offsetDays,
          },
          from: {
            unit: windowUnit,
            value: offsetDays + windowValue,
          },
          rolling: true,
        };
      } else if (windowValue === 0) {
        return {
          from: START_OF_TODAY,
          to: END_OF_TODAY,
          type: DateRangeType.Between,
        };
      }
      return {
        type,
        window: {unit: windowUnit, value: windowValue},
      };
    }
    case DateRangeType.Between:
    case DateRangeType.NotBetween: {
      return {
        ...getBetweenDateRangeAttributes(fromDate, toDate, times),
        type,
      };
    }
    case DateRangeType.On:
    case DateRangeType.NotOn: {
      return {
        from: getBetweenDateRangeAttributes(fromDate, toDate, times).from,
        to: getBetweenDateRangeAttributes(fromDate, toDate, times).from,
        type,
      };
    }
    case DateRangeType.Since: {
      return getSinceDateRangeAttributes(toDate.isBefore(fromDate, `day`) ? toDate : fromDate);
    }
    case DateRangeType.Before: {
      return {
        type,
        to: getSinceDateRangeAttributes(fromDate).from,
      };
    }
  }
  return null;
}

export function getSimilarDateRangeForPickerMode(
  dateRange: DateRange,
  asOfDate: moment.Moment,
  pickerMode: DatePickerMode,
  timezone?: Timezone,
  times?: {fromTime: moment.Moment; toTime: moment.Moment},
): DateRangeAttributes | null {
  const defaultDateRangeType: DateRangeType = (() => {
    switch (pickerMode) {
      case DatePickerMode.InTheLast:
        return DateRangeType.RelativeAfter;
      case DatePickerMode.RelativeFuture:
        return DateRangeType.RelativeFuture;
      case DatePickerMode.On:
        return DateRangeType.On;
      case DatePickerMode.Between:
        return DateRangeType.Between;
      case DatePickerMode.Since:
        return DateRangeType.Since;
      default: {
        const unreachable: never = pickerMode; // a trick to enforce full enum coverage in switch case
        throw new Error(`unknown picker mode ${unreachable}`);
      }
    }
  })();
  return getSimilarDateRange(dateRange, asOfDate, defaultDateRangeType, timezone, times);
}

function generateCommonDateRangeTrackingProps(
  dateRange: DateRange | undefined,
  projectAsOfDate: string | null,
  projectTz: Timezone | undefined,
  timePeriodSettings: TimePeriodSettings | undefined,
) {
  if (!dateRange) {
    return {
      'Date range from': null,
      'Date range to': null,
      'Date window duration (in days)': null,
      'Date window units selection': null,
    };
  }

  const {from, to} = dateRange.getCalendarDatesAsOf(
    projectAsOfDate,
    CALENDAR_DATE_FORMAT,
    projectTz,
    timePeriodSettings,
  );
  const fromDate = moment(parseDate(from));
  const toDate = moment(parseDate(to));
  const dateRangePickerMode = getDatePickerMode(dateRange);
  return {
    'Date picker mode': getDatePickerMode(dateRange),
    'Date range from': fromDate.toISOString(),
    'Date range to': toDate.toISOString(),
    'Date window duration (in days)': toDate.diff(fromDate, `day`),
    'Date window units selection': [DatePickerMode.InTheLast, DatePickerMode.RelativeFuture].includes(
      dateRangePickerMode,
    )
      ? getRelativeWindowUnit(dateRange)
      : null,
    'Date window value': dateRange.window?.value ?? (dateRange.from as RelativeDate)?.value ?? null,
    'Date window offset value': (dateRange.to as RelativeDate)?.value ?? null,
    'Is rolling': dateRange.rolling ?? false,
    'Future from date': dateRange.hasFutureFromDate(
      projectTz ?? getLocalTimezone(),
      projectAsOfDate,
      timePeriodSettings ?? DEFAULT_TIME_PERIOD_SETTINGS,
    ),
    'Future to date': dateRange.hasFutureToDate(
      projectTz ?? getLocalTimezone(),
      projectAsOfDate,
      timePeriodSettings ?? DEFAULT_TIME_PERIOD_SETTINGS,
    ),
  };
}

export function generateDatePickerTrackingProps({
  dateRange,
  reportName,
  pickerMode,
  projectAsOfDate,
  trackedUseCase,
  projectTz,
  timePeriodSettings,
  additionalTrackingProps,
}: {
  dateRange: DateRange;
  reportName: string;
  pickerMode: DatePickerMode;
  projectAsOfDate: moment.Moment | string | Date | null;
  trackedUseCase: string;
  projectTz: Timezone | undefined;
  timePeriodSettings: TimePeriodSettings | undefined;
  additionalTrackingProps?: Record<string, any>;
}): any {
  const asOfMoment = getCurrentMomentAsOf(projectAsOfDate, projectTz);
  const commonTrackingProps = generateCommonDateRangeTrackingProps(
    dateRange,
    asOfMoment.format(CALENDAR_DATE_FORMAT),
    projectTz,
    timePeriodSettings,
  );
  return {
    ...commonTrackingProps,
    ...additionalTrackingProps,
    'Date picker mode': commonTrackingProps[`Date picker mode`] ?? pickerMode,
    tab: reportName,
    'usage context': trackedUseCase,
  };
}

function getDisplayLabelForDateString({
  date,
  projectTz,
  includeTime,
  formatMode,
}: {
  date: string;
  projectTz: Timezone;
  includeTime: boolean;
  formatMode: DateRangeLabelFormatMode;
}): Translatable {
  if (date in SPECIAL_DATE_DISPLAY_NAMES) {
    return SPECIAL_DATE_DISPLAY_NAMES[date];
  }
  const jsDate = momentTimezone.tz(date, includeTime ? TIMESTAMP_FORMAT : CALENDAR_DATE_FORMAT, projectTz).toDate();
  return ({formatDate}) =>
    formatDate(jsDate, {
      day: `numeric`,
      month: formatMode === `numeric` ? `numeric` : `short`,
      year: formatMode === `numeric` ? `2-digit` : `numeric`,
      hour: includeTime ? `numeric` : undefined,
      minute: includeTime ? `numeric` : undefined,
      timeZone: projectTz,
    });
}

export function getDateRangeLabel({
  dateRange,
  projectTz,
  formatMode = `simple`,
}: {
  dateRange: null | DateRange;
  projectTz: Timezone | undefined;
  formatMode?: DateRangeLabelFormatMode;
}): Translatable {
  if (!dateRange) {
    return `Select Date`;
  }

  // RelativeAfter presets are treated like normal date ranges for the purposes of formatting options.
  // We ignore the `matchingPresetLabel` for these.
  //
  // Note: pickerMode treats Today and Yesterday presets as `in the last` date ranges, so we look at the
  // actually dateRange.type instead to avoid formatting these as `in the last 0 days` and
  // `in the last 0 days ending 1 day ago`, respectively.
  let matchingPresetLabel = null;
  if (dateRange.type !== DateRangeType.RelativeAfter) {
    // If no matching preset label was specified by the caller, try to get the matching preset via
    // the DateRangeAttributes
    const presetDateRange = getPresetDateRangeFromAttributes(dateRange);
    matchingPresetLabel = presetDateRange ? PRESET_DATE_RANGE_DISPLAY_NAMES[presetDateRange] : null;
  }
  if (matchingPresetLabel) {
    return ({print}) => `${print(matchingPresetLabel)}`;
  }

  if (!projectTz) {
    projectTz = getLocalTimezone();
  }
  const includeTime = dateRange.hasSubDayGranularity(projectTz);
  const fromDateType = getDateType(dateRange.from);
  const toDateType = getDateType(dateRange.to);
  const relativeFrom: RelativeDate | null = fromDateType === `relative` ? (dateRange.from as RelativeDate) : null;
  const relativeTo: RelativeDate | null = toDateType === `relative` ? (dateRange.to as RelativeDate) : null;

  let fromLabel =
    typeof dateRange.from === `string`
      ? getDisplayLabelForDateString({date: dateRange.from, projectTz, includeTime, formatMode})
      : null;
  let toLabel =
    typeof dateRange.to === `string`
      ? getDisplayLabelForDateString({date: dateRange.to, projectTz, includeTime, formatMode})
      : null;

  const exclusionPreset = exclusionOffsetToPreset(dateRange.exclusionOffset);
  if (exclusionPreset !== ExclusionPreset.NoExclusion) {
    const exclusionWindow = (dateRange.exclusionOffset as RelativeDate) ?? null;
    toLabel = (i18n) => EXCLUSION_SELECTED_LABEL[exclusionPreset](i18n, exclusionWindow).toLowerCase();
  }

  // Special case:
  // If we have the case where one date is relative and the other is not, resolve it as separately for better control over
  // the formatting than we have in `InTheLast`.
  const dateTypes = [fromDateType, toDateType];
  if (!dateTypes.includes(`null`) && dateTypes.filter((item) => item === `relative`).length === 1) {
    if (relativeFrom) {
      fromLabel = ({t, print}) =>
        t(`reports.date-range.display-label.last`, {
          value: relativeFrom.value,
          unit: print(TimeUnitLabelTranslatable[relativeFrom.unit](relativeFrom.value)).toLowerCase(),
        });
      const endingLabel = ({t, print}) => t(`reports.date-range.display-label.ending`, {date: print(toLabel)});
      return ({print}) => `${print(fromLabel)} ${print(endingLabel)}`;
    }
    if (relativeTo) {
      // For verbal clarity, in this case we use the format of `Since {date} excluding last X units`
      const startLabel = ({t, print}) => t(`reports.date-range.display-label.since`, {date: print(fromLabel)});
      toLabel = ({t, print}) =>
        t(`reports.date-range.exclusion.custom.selected-label`, {
          value: relativeTo.value,
          unit: print(TimeUnitLabelTranslatable[relativeTo.unit](relativeTo.value)).toLowerCase(),
        });
      return ({print}) => `${print(startLabel)} ${print(toLabel).toLowerCase()}`;
    }
  }

  const pickerMode = getDatePickerMode(dateRange);
  switch (pickerMode) {
    case DatePickerMode.On:
      return fromLabel;
    case DatePickerMode.Since: {
      if ([`sentence`, `simple`].includes(formatMode) || dateRange.exclusionOffset) {
        const rangeLabel = ({t, print}) => t(`reports.date-range.display-label.since`, {date: print(fromLabel)});
        // For clarity, we use always the format of `Since {date} excluding {exclusionOffset}` when an exclusionOffset is specified.
        if (dateRange.exclusionOffset) {
          return ({print}) => `${print(rangeLabel)} ${print(toLabel).toLowerCase()}`;
        }
        return rangeLabel;
      }
      return fromLabel;
    }
    case DatePickerMode.Between: {
      if (typeof dateRange.from === `string` && fromDateType === `absolute` && dateRange.from === dateRange.to) {
        // When we see the same absolute date twice, we return it only once.
        return fromLabel;
      }
      if (
        includeTime &&
        typeof dateRange.from === `string` &&
        typeof dateRange.to === `string` &&
        fromDateType === `absolute` &&
        toDateType === `absolute`
      ) {
        const fromDateTime = dateRange.from.split(`T`);
        const toDateTime = dateRange.to.split(`T`);
        // If this is the same calendar date, don't include the calendar date a second time in the toLabel.
        if (fromDateTime[0] === toDateTime[0]) {
          const jsDate = momentTimezone.tz(dateRange.to, TIMESTAMP_FORMAT, projectTz).toDate();
          const toTime = ({formatDate}) =>
            formatDate(jsDate, {
              hour: includeTime ? `numeric` : undefined,
              minute: includeTime ? `numeric` : undefined,
              timeZone: projectTz,
            });
          return ({print}) => `${print(fromLabel)} — ${print(toTime)}`;
        }
      }
      if (formatMode === `sentence`) {
        return ({t, print}) =>
          t(`reports.date-range.display-label.between`, {fromDate: print(fromLabel), toDate: print(toLabel)});
      }
      return ({print}) => `${print(fromLabel)} — ${print(toLabel)}`;
    }
    case DatePickerMode.RelativeFuture: {
      if (!dateRange.window) {
        return `unknown`;
      }
      return ({print}) =>
        `${dateRange.window.value} ${print(TimeUnitLabelTranslatable[dateRange.window.unit](dateRange.window.value)).toLowerCase()}`;
    }
    case DatePickerMode.InTheLast: {
      let fromWindow = dateRange.window;
      if (relativeFrom && relativeTo && relativeFrom.unit === relativeTo.unit) {
        // un-offset the start by end for display in text.
        // Note: the units should always match. there's no entry point in the UX or Copilot that allows
        // rolling dates of this shape with mismatched units. mismatched units are instead routed to the
        // exclusion path by copilot to avoid confusing users.
        fromWindow = {unit: relativeFrom.unit, value: relativeFrom.value - relativeTo.value};
      } else if (relativeFrom) {
        fromWindow = relativeFrom;
      }

      if (formatMode === `sentence`) {
        fromLabel = ({t, print}) =>
          t(`reports.date-range.display-label.in-the-last`, {
            value: fromWindow.value,
            unit: print(TimeUnitLabelTranslatable[fromWindow.unit](fromWindow.value)).toLowerCase(),
          });
      } else if (formatMode === `simple` || dateRange.exclusionOffset) {
        // For verbal clarity, we at least prefix the label with `Last` in the presence of an exclusion because
        // 7 days excluding today sounds unclear.
        fromLabel = ({t, print}) =>
          t(`reports.date-range.display-label.last`, {
            value: fromWindow.value,
            unit: print(TimeUnitLabelTranslatable[fromWindow.unit](fromWindow.value)).toLowerCase(),
          });
      } else {
        fromLabel = ({print}) =>
          `${fromWindow.value} ${print(TimeUnitLabelTranslatable[fromWindow.unit](fromWindow.value)).toLowerCase()}`;
      }

      if (relativeTo) {
        toLabel = ({t, print}) =>
          t(`reports.date-range.display-label.ending-ago`, {
            value: relativeTo.value,
            unit: print(TimeUnitLabelTranslatable[relativeTo.unit](relativeTo.value)).toLowerCase(),
          });
      }
      if (toLabel) {
        return ({print}) => `${print(fromLabel)} ${print(toLabel)}`;
      }
      return fromLabel;
    }
    default: {
      throw new Error(`"date-range" attribute contains DateRange info with unsupported DateRangeType`);
    }
  }
}

export function getRelativeInputMax(unit: TimeUnit): number | null {
  if (unit === TimeUnit.Hour) {
    return LAST_HOURS_MAX;
  }
  if (unit === TimeUnit.Minute) {
    return LAST_MINUTES_MAX;
  }
  return null;
}

export function getRelativeInputErrorMessage(
  value: number | null | undefined,
  unit: TimeUnit,
  shouldEnforceMax: boolean,
): Translatable {
  if (Number.isNaN(value) || (value && !Number.isInteger(value))) {
    return ({t}) => t(`reports.date-range.invalid.last-non-integer-input`);
  }
  if (value < 0) {
    return ({t}) =>
      t(`reports.date-range.invalid.last-range-min`, {
        min: 0,
        unit,
      });
  }

  if (shouldEnforceMax) {
    const max: number = getRelativeInputMax(unit);
    if (max && value > max) {
      return ({t}) =>
        t(`reports.date-range.invalid.last-range-max`, {
          max,
          unit,
        });
    }
  }
  return ``;
}
